const asId = (value) => {
  if (!value) return "";
  if (typeof value === "object") {
    return String(value._id || value.classId || value.sectionId || "");
  }
  return String(value);
};

const isPrimaryClassName = (name) =>
  String(name || "").trim().toLowerCase() === "primary";

/**
 * Primary class/section plus additionalClassAndSection, merged by class id.
 * allSections=true means the teacher may use every section in that class.
 */
export const getTeacherAssignedClasses = (teacher) => {
  const byId = new Map();

  const addClass = (classId, className, sectionIds, allSections) => {
    const id = asId(classId);
    if (!id) return;
    const existing = byId.get(id) || {
      _id: id,
      className: "",
      sectionIds: new Set(),
      allSections: false,
    };
    if (className) existing.className = className;
    if (allSections) existing.allSections = true;
    (sectionIds || []).forEach((sectionId) => {
      const sid = asId(sectionId);
      if (sid) existing.sectionIds.add(sid);
    });
    byId.set(id, existing);
  };

  const primaryClassId = teacher?.classId?._id || teacher?.classId;
  if (primaryClassId) {
    const primarySectionId = teacher?.sectionId?._id || teacher?.sectionId;
    addClass(
      primaryClassId,
      teacher?.classId?.className,
      primarySectionId ? [primarySectionId] : [],
      !primarySectionId
    );
  }

  (teacher?.additionalClassAndSection || []).forEach((item) => {
    const classId = item?.class?.classId?._id || item?.class?.classId;
    const className = item?.class?.className;
    const sections = Array.isArray(item?.sectionId) ? item.sectionId : [];
    if (!sections.length) {
      addClass(classId, className, [], true);
      return;
    }
    addClass(
      classId,
      className,
      sections.map((s) => s?.sectionId?._id || s?.sectionId),
      false
    );
  });

  return Array.from(byId.values()).map((item) => ({
    _id: item._id,
    className: item.className,
    allSections: item.allSections,
    sectionIds: Array.from(item.sectionIds),
  }));
};

/** Language menu: Primary class on the main assignment or any extra assignment. */
export const teacherHasLanguageAccess = (teacher) => {
  const classes = getTeacherAssignedClasses(teacher);
  if (!classes.length) return false;
  if (classes.some((cls) => isPrimaryClassName(cls.className))) return true;
  return (
    Array.isArray(teacher?.additionalClassAndSection) &&
    teacher.additionalClassAndSection.length > 0
  );
};

export const filterSectionsForAssignedClass = (assignedClasses, classId, sections) => {
  const assigned = (assignedClasses || []).find((cls) => String(cls._id) === String(classId));
  const list = Array.isArray(sections) ? sections : [];
  if (!assigned || assigned.allSections || !assigned.sectionIds.length) return list;
  const allowed = new Set(assigned.sectionIds.map(String));
  return list.filter((section) => allowed.has(String(section._id)));
};
